import { useState, useEffect, useRef } from 'react';
import { ScrollReveal } from './ScrollReveal';
import { Crown, TrendingUp, Target, Settings, PieChart, Scale, Search, Headphones, Package, Lock, ArrowRight, Zap, Database, GitBranch, Route } from 'lucide-react';

const AGENTS = [
  { icon: Crown, name: 'Executive Agent', forWho: 'CEO, founders, leadership',
    tasks: ['Structure daily priorities','Summarize key emails and updates','Prepare meeting briefings','Extract action items from meetings','Track open decisions','Build executive intelligence packs'],
    examples: ['Everything I need to know today in 2 minutes.','Summarize open sales, legal, and finance points for the 3 PM meeting.','Prepare a briefing for my investor call.','Which decisions are waiting for my approval?'],
    why: 'Turns executive chaos into structured leadership clarity.' },
  { icon: TrendingUp, name: 'Sales Agent', forWho: 'Sales teams, account managers',
    tasks: ['Analyze and score new leads','Enrich leads with company intelligence','Prepare follow-up drafts','Support proposal creation','Signal stalled warm deals','Generate pipeline summaries'],
    examples: ['Which leads this week have the highest potential?','Draft follow-ups for everyone who hasn\'t replied in 7 days.','Summarize this prospect into a sales briefing.','Which warm deals are losing momentum?'],
    why: 'Helps sales teams move faster, more consistently, and more intelligently.' },
  { icon: Target, name: 'Marketing Agent', forWho: 'Marketing, content, brand teams',
    tasks: ['Generate content ideas','Draft social media posts','Prepare campaign copy','Analyze competitor messaging','Structure content calendars','Monitor performance patterns'],
    examples: ['Create 10 content ideas for family offices.','Write 3 premium LinkedIn posts for this week.','Analyze messaging of our 5 biggest competitors.','Build a campaign flow for a demo funnel.'],
    why: 'Produces faster without starting from zero every time.' },
  { icon: Settings, name: 'Operations Agent', forWho: 'Operations, project coordination',
    tasks: ['Structure and track tasks','Detect workflow bottlenecks','Collect open action points','Create status overviews','Signal operational issues','Accelerate administrative flows'],
    examples: ['Which tasks are stuck longer than 5 days?','Give me all open operational issues.','Which departments are blocking others?','Turn these notes into a workable action list.'],
    why: 'Reduces friction in daily operations across teams.' },
  { icon: PieChart, name: 'Finance Agent', forWho: 'Finance teams, controllers',
    tasks: ['Organize invoice flows','Structure spending overviews','Detect payment anomalies','Prepare cashflow snapshots','Build management summaries','Support month-end reporting'],
    examples: ['Which open payments need attention today?','Create a cost overview by department.','Which invoices appear unusual?','Prepare a management note for the finance meeting.'],
    why: 'Generates faster insight with less manual preparation.' },
  { icon: Scale, name: 'Legal / Compliance Agent', forWho: 'Legal teams, compliance officers',
    tasks: ['Screen contracts for risks','Highlight critical clauses','Compare against standard terms','Organize due diligence documents','Build compliance checklists','Signal missing documentation'],
    examples: ['Highlight the riskiest clauses in this contract.','Compare this version against our standard terms.','Which documents are missing for this file?','Create a due diligence checklist for this deal.'],
    why: 'Accelerates legal preparation without replacing legal judgment.' },
  { icon: Search, name: 'Research Agent', forWho: 'Strategy, M&A, business development',
    tasks: ['Perform market research','Compare competitors','Summarize complex dossiers','Collect sector intelligence','Scan news and trends','Create briefing papers'],
    examples: ['Give me a one-page summary of this market.','Compare 5 competitors on pricing and positioning.','What opportunities are emerging in this sector?','What are the main risks in this project?'],
    why: 'Turns hours of searching into minutes of clarity.' },
  { icon: Headphones, name: 'Customer Service Agent', forWho: 'Support teams, service desks',
    tasks: ['Classify incoming tickets','Prepare response drafts','Flag urgent cases','Handle FAQ-style requests','Summarize customer history','Signal SLA risks'],
    examples: ['Which support cases are urgent right now?','Draft a response for this customer complaint.','Which tickets are waiting too long?','Which questions come back most often?'],
    why: 'Improves service speed, consistency, and coordination.' },
  { icon: Package, name: 'Procurement Agent', forWho: 'Procurement, vendor management',
    tasks: ['Organize supplier data','Compare vendor proposals','Track contract expirations','Structure vendor onboarding','Highlight price differences','Prepare purchasing workflows'],
    examples: ['Compare these 3 suppliers on terms and pricing.','Which vendor contracts expire within 30 days?','Which documents are still missing for onboarding?','Rank vendors based on these criteria.'],
    why: 'Enables cleaner and faster procurement decisions.' },
  { icon: Lock, name: 'Private Office Agent', forWho: 'Family offices, UHNW, principals',
    tasks: ['Manage schedules and priorities','Prepare travel briefing packs','Gather documents across entities','Organize contacts per dossier','Coordinate private and business matters','Create multi-entity overviews'],
    examples: ['Build my briefing pack for tomorrow in London.','Gather all open action points across 3 companies.','Which documents do I need for this appointment?','Which tasks overlap across entities?'],
    why: 'Ideal where private, holding, and operating structures overlap.' },
];

const CORE_FUNCTIONS = [
  { icon: Search, label: 'Retrieve', desc: 'Find the right information fast across all sources.' },
  { icon: GitBranch, label: 'Structure', desc: 'Turn disorder into organized, actionable clarity.' },
  { icon: Zap, label: 'Prepare', desc: 'Create the first useful version of what is needed.' },
  { icon: Route, label: 'Route', desc: 'Send it to the right person or approval step.' },
];

export default function AgentsInPractice() {
  const [selected, setSelected] = useState(0);
  const [typedExample, setTypedExample] = useState('');
  const [exampleIdx, setExampleIdx] = useState(0);
  const typingRef = useRef(null);
  const agent = AGENTS[selected];

  useEffect(() => {
    setExampleIdx(0);
    setTypedExample('');
  }, [selected]);

  useEffect(() => {
    const example = agent.examples[exampleIdx] || '';
    let charIdx = 0;
    setTypedExample('');
    if (typingRef.current) clearInterval(typingRef.current);
    typingRef.current = setInterval(() => {
      if (charIdx <= example.length) {
        setTypedExample(example.substring(0, charIdx));
        charIdx++;
      } else {
        clearInterval(typingRef.current);
        setTimeout(() => {
          setExampleIdx(prev => (prev + 1) % agent.examples.length);
        }, 2500);
      }
    }, 30);
    return () => clearInterval(typingRef.current);
  }, [exampleIdx, agent.examples]);

  const Icon = agent.icon;

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="agents-in-practice-section">
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-30" />
      <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-aurocle-gold/4 rounded-full blur-[200px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-0 w-[300px] h-[300px] bg-aurocle-gold/3 rounded-full blur-[160px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Intro */}
        <ScrollReveal>
          <div className="text-center mb-6">
            <div className="overline mb-4">Agents in Practice</div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-aurocle-text mb-4">
              What your agents actually do,{' '}
              <span className="font-accent italic text-aurocle-gold">every day.</span>
            </h2>
            <p className="text-base text-aurocle-platinum-muted max-w-3xl mx-auto mb-2">
              Not vague AI. Practical digital specialists that retrieve, structure, prepare, and route work across your business — from executive briefings to legal review, sales follow-up to operations coordination.
            </p>
          </div>
        </ScrollReveal>

        {/* 4 Core Functions */}
        <ScrollReveal delay={100}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-16 max-w-3xl mx-auto">
            {CORE_FUNCTIONS.map((fn, i) => {
              const FnIcon = fn.icon;
              return (
                <div key={i} className="text-center p-4 border border-aurocle-gold/10 bg-aurocle-surface/30 group hover:border-aurocle-gold/30 transition-all">
                  <FnIcon size={20} className="text-aurocle-gold mx-auto mb-2 group-hover:scale-110 transition-transform" />
                  <div className="text-xs font-heading font-medium text-aurocle-text">{fn.label}</div>
                  <div className="text-[10px] text-aurocle-platinum-muted mt-1">{fn.desc}</div>
                </div>
              );
            })}
          </div>
        </ScrollReveal>

        {/* Agent Selector + Detail */}
        <ScrollReveal delay={150}>
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {AGENTS.map((a, i) => {
              const AIcon = a.icon;
              return (
                <button key={i} onClick={() => setSelected(i)} data-testid={`practice-agent-${i}`}
                  className={`flex items-center gap-2 px-4 py-2.5 text-xs font-mono uppercase tracking-wider transition-all border ${
                    selected === i
                      ? 'bg-aurocle-gold/10 border-aurocle-gold/30 text-aurocle-gold shadow-[0_0_15px_rgba(212,175,55,0.1)]'
                      : 'border-white/6 text-aurocle-text-secondary hover:border-white/15 hover:text-aurocle-text'}`}>
                  <AIcon size={12} /> {a.name.replace(' Agent', '')}
                </button>
              );
            })}
          </div>
        </ScrollReveal>

        {/* Agent Detail Panel */}
        <ScrollReveal delay={200}>
          <div className="glass-card border-aurocle-gold/10 overflow-hidden relative" data-testid="agent-practice-detail">
            <div className="absolute inset-0 moving-bg" style={{background:'linear-gradient(135deg, rgba(212,175,55,0.03) 0%, transparent 40%, rgba(212,175,55,0.02) 80%, transparent 100%)'}} />
            <div className="relative p-8 lg:p-10">
              <div className="grid lg:grid-cols-2 gap-10">
                {/* Left: Info */}
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 flex items-center justify-center border border-aurocle-gold/20 agent-working">
                      <Icon size={24} className="text-aurocle-gold" />
                    </div>
                    <div>
                      <h3 className="font-heading text-xl font-medium text-aurocle-text">{agent.name}</h3>
                      <p className="text-xs text-aurocle-gold/60 font-mono uppercase tracking-wider">{agent.forWho}</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <div className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-3">Daily Tasks</div>
                    <div className="space-y-2">
                      {agent.tasks.map((task, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm text-aurocle-text group">
                          <div className="w-1.5 h-1.5 rounded-full bg-aurocle-gold/40 group-hover:bg-aurocle-gold transition-colors shrink-0" />
                          {task}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="p-4 bg-aurocle-gold/5 border border-aurocle-gold/10">
                    <div className="text-xs font-mono text-aurocle-gold/60 uppercase tracking-wider mb-1">Why This Matters</div>
                    <p className="text-sm text-aurocle-text font-medium">{agent.why}</p>
                  </div>
                </div>

                {/* Right: Live prompt typewriter */}
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary mb-3">Practical Examples</div>
                  <div className="bg-aurocle-base/70 border border-white/5 p-6 mb-4 min-h-[80px]">
                    <div className="text-xs font-mono text-aurocle-gold/40 mb-2">{'>'} agent prompt</div>
                    <div className="text-sm text-aurocle-text font-mono leading-relaxed">
                      "{typedExample}<span className="cursor-blink" />"
                    </div>
                  </div>

                  <div className="space-y-2">
                    {agent.examples.map((ex, i) => (
                      <div key={i} className={`p-3 text-xs border transition-all cursor-pointer ${
                        exampleIdx === i
                          ? 'bg-aurocle-gold/5 border-aurocle-gold/15 text-aurocle-text'
                          : 'border-white/4 text-aurocle-text-secondary hover:border-white/10'
                      }`} onClick={() => setExampleIdx(i)}>
                        <span className="font-mono text-aurocle-gold/40 mr-2">{'>'}</span>{ex}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal delay={300}>
          <div className="mt-12 text-center">
            <p className="text-sm text-aurocle-platinum-muted mb-6">
              If this is the kind of work your teams handle every day, Aurocle Agents can be structured around your business.
            </p>
            <div className="flex items-center justify-center gap-4">
              <a href="#contact" className="btn-gold cta-burning" data-testid="practice-cta-demo">
                Request Private Demo <ArrowRight size={14} />
              </a>
              <a href="#contact" className="btn-outline cta-outline-glow" data-testid="practice-cta-meeting">
                Book Digital Meeting
              </a>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
